"use client";

import Link from "next/link";
import ButtonLink from "@/components/site/button-link";
import DirectionalExternalArrow from "@/components/ui/DirectionalExternalArrow";
import { buttonVariants } from "@/components/ui/button-variants";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { NavExternalLabel, resolveHref } from "@/components/layout/nav-shared";
import type { NavGroup } from "@/lib/content/types";
import { type Locale } from "@/lib/i18n/config";
import { cn } from "@/lib/utils";

function getNavGroupColumns(group: NavGroup): 2 | 3 {
  if (group.columns) {
    return group.columns;
  }

  if (group.items.length === 6 && group.footer) {
    return 3;
  }

  return 2;
}

function DesktopNavGroup({
  group,
  locale,
}: {
  group: NavGroup;
  locale: Locale;
}) {
  const columns = getNavGroupColumns(group);

  return (
    <NavigationMenuItem>
      <NavigationMenuTrigger>{group.label}</NavigationMenuTrigger>
      <NavigationMenuContent>
        <div
          className={cn(
            columns === 3
              ? "w-[min(48rem,calc(100vw-2rem))]"
              : "w-[min(36rem,calc(100vw-2rem))]",
            group.footer && "md:grid md:grid-cols-[1fr_13.5rem]",
          )}
        >
          <ul
            className={cn(
              "grid gap-1 p-3",
              columns === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2",
            )}
          >
            {group.items.map((item) => (
              <li key={item.href}>
                <NavigationMenuLink
                  className="h-full min-h-0 gap-1 p-3"
                  render={
                    item.external ? (
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                      />
                    ) : (
                      <Link href={resolveHref(item.href, locale)} />
                    )
                  }
                >
                  <NavExternalLabel
                    label={item.label}
                    external={item.external}
                    className="text-sm font-medium leading-5"
                  />
                  {item.description ? (
                    <span className="block text-xs leading-5 text-muted-foreground">
                      {item.description}
                    </span>
                  ) : null}
                </NavigationMenuLink>
              </li>
            ))}
          </ul>

          {group.footer ? (
            <div className="flex flex-col justify-between gap-4 border-t p-4 md:border-t-0 md:border-l">
              <div>
                <p className="text-sm font-medium leading-5">
                  {group.footer.title}
                </p>
                <p className="mt-1 text-xs leading-5 text-muted-foreground">
                  {group.footer.description}
                </p>
              </div>
              <ButtonLink
                href={group.footer.ctaHref}
                external={group.footer.external}
                variant="outline"
                size="sm"
                className="nav-dropdown-cta w-full"
              >
                {group.footer.ctaLabel}
                {group.footer.external ? (
                  <DirectionalExternalArrow className="size-3.5" />
                ) : null}
              </ButtonLink>
            </div>
          ) : null}
        </div>
      </NavigationMenuContent>
    </NavigationMenuItem>
  );
}

export default function DesktopNavigation({
  locale,
  mainNavigation,
  pricingNavLink,
}: {
  locale: Locale;
  mainNavigation: NavGroup[];
  pricingNavLink: { href: string; label: string };
}) {
  return (
    <NavigationMenu className="flex-none">
      <NavigationMenuList>
        {mainNavigation.map((group) => (
          <DesktopNavGroup key={group.label} group={group} locale={locale} />
        ))}
        <NavigationMenuItem>
          <NavigationMenuLink
            render={<Link href={resolveHref(pricingNavLink.href, locale)} />}
            className={cn(buttonVariants({ variant: "ghost", size: "sm" }))}
          >
            {pricingNavLink.label}
          </NavigationMenuLink>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  );
}
